import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from './adminApi';
import { Plus, Edit2, Trash2, Search, Briefcase, ExternalLink, ArrowUp, ArrowDown, Save } from 'lucide-react';

const AdminCaseStudies = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('All');
  const [reordering, setReordering] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  const refresh = () => {
    setLoading(true);
    adminApi
      .listCaseStudies()
      .then((data) => {
        // Sort by displayOrder ascending; missing displayOrder go last
        data.sort(
          (a, b) =>
            (a.displayOrder ?? 9999) - (b.displayOrder ?? 9999) || (a.id || '').localeCompare(b.id || '')
        );
        setCases(data);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  const move = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= cases.length) return;
    const next = [...cases];
    [next[index], next[target]] = [next[target], next[index]];
    setCases(next);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await adminApi.reorderCaseStudies(cases.map((c) => c.id));
      setReordering(false);
    } finally {
      setSavingOrder(false);
    }
  };

  const industries = useMemo(
    () => ['All', ...new Set(cases.map((c) => c.industry).filter(Boolean))],
    [cases]
  );

  const filtered = useMemo(() => {
    let list = cases;
    if (filter !== 'All') list = list.filter((c) => c.industry === filter);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.client.toLowerCase().includes(q) ||
          (c.industry || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [cases, filter, query]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this case study permanently?')) return;
    await adminApi.deleteCaseStudy(id);
    setCases((prev) => prev.filter((c) => c.id !== id));
  };

  return (
    <div className="px-8 lg:px-12 py-10" data-testid="admin-cases-page">
      <header className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="text-[#83BF42] text-xs uppercase tracking-[0.2em] font-['Inter'] mb-2">Content</p>
          <h1 className="text-white text-4xl font-light font-['Inter']">Case Studies</h1>
          <p className="text-white/40 text-sm font-['Lato'] mt-2">
            Manage every client success story shown on `/work`.
          </p>
        </div>
        <div className="flex gap-2">
          {reordering ? (
            <>
              <button
                onClick={() => { setReordering(false); refresh(); }}
                data-testid="cases-reorder-cancel"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/10 text-white/80 text-sm font-['Inter'] hover:bg-white/5 transition"
              >
                Cancel
              </button>
              <button
                onClick={saveOrder}
                disabled={savingOrder}
                data-testid="cases-reorder-save"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#83BF42] text-[#0F0F11] text-sm font-medium font-['Inter'] hover:bg-[#A8D96C] transition disabled:opacity-50"
              >
                <Save className="w-4 h-4" /> {savingOrder ? 'Saving…' : 'Save order'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => { setReordering(true); setQuery(''); setFilter('All'); }}
                data-testid="cases-reorder-toggle"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/10 text-white/80 text-sm font-['Inter'] hover:bg-white/5 transition"
              >
                <ArrowUp className="w-4 h-4" /> Reorder
              </button>
              <Link
                to="/admin/cases/new"
                data-testid="cases-new-button"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#83BF42] text-[#0F0F11] text-sm font-medium font-['Inter'] hover:bg-[#A8D96C] transition"
              >
                <Plus className="w-4 h-4" /> New case study
              </Link>
            </>
          )}
        </div>
      </header>

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div className="flex flex-wrap gap-2">
          {industries.map((ind) => (
            <button
              key={ind}
              onClick={() => setFilter(ind)}
              data-testid={`cases-filter-${ind.toLowerCase().replace(/[\s/]+/g, '-')}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-['Inter'] transition-all ${
                filter === ind
                  ? 'bg-[#83BF42] text-[#0F0F11]'
                  : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
              }`}
            >
              {ind}
            </button>
          ))}
        </div>
        <div className="relative w-full lg:w-72">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search case studies…"
            data-testid="cases-search"
            className="w-full pl-11 pr-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-white placeholder-white/30 font-['Lato'] text-sm focus:outline-none focus:border-[#83BF42] transition-colors"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#83BF42] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#15151A] border border-white/5 rounded-2xl py-16 text-center" data-testid="cases-empty">
          <Briefcase className="w-8 h-8 text-white/20 mx-auto mb-3" />
          <p className="text-white/50 text-sm font-['Lato']">No case studies found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((c) => (
            <article
              key={c.id}
              data-testid={`case-card-${c.id}`}
              className="bg-[#15151A] border border-white/5 rounded-2xl overflow-hidden flex flex-col hover:border-[#83BF42]/30 transition-colors"
            >
              <div className="relative h-32 overflow-hidden bg-black/40">
                {c.heroImage && <img src={c.heroImage} alt={c.title} className="w-full h-full object-cover" />}
                <div className="absolute top-3 left-3">
                  <span className="bg-black/50 backdrop-blur-sm text-[#83BF42] text-[10px] font-bold font-['Inter'] px-2.5 py-1 rounded-full uppercase tracking-wider">
                    {c.industry}
                  </span>
                </div>
                {c.statHighlight && (
                  <div className="absolute bottom-3 left-3 text-[#83BF42] text-2xl font-bold font-['Inter']">
                    {c.statHighlight}
                  </div>
                )}
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <p className="text-white/40 text-[10px] uppercase tracking-wider font-['Inter']">
                  {c.client}
                </p>
                <h3 className="text-white text-base font-medium font-['Inter'] leading-snug line-clamp-2 mt-1 mb-2">
                  {c.title}
                </h3>
                <p className="text-white/40 text-xs font-['Lato'] mb-4 line-clamp-2 flex-1">
                  {c.description}
                </p>
                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  {reordering ? (
                    <div className="flex items-center gap-1 w-full" data-testid={`case-reorder-controls-${c.id}`}>
                      <button
                        onClick={() => move(filtered.indexOf(c), -1)}
                        disabled={filtered.indexOf(c) === 0}
                        data-testid={`case-move-up-${c.id}`}
                        className="p-2 rounded text-white/60 hover:text-white hover:bg-white/5 disabled:opacity-30"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => move(filtered.indexOf(c), 1)}
                        disabled={filtered.indexOf(c) === filtered.length - 1}
                        data-testid={`case-move-down-${c.id}`}
                        className="p-2 rounded text-white/60 hover:text-white hover:bg-white/5 disabled:opacity-30"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                      <span className="ml-auto text-white/40 text-xs font-['Lato']">
                        Position {filtered.indexOf(c) + 1}
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/admin/cases/${c.id}`}
                          data-testid={`case-edit-${c.id}`}
                          className="inline-flex items-center gap-1.5 text-[#83BF42] text-xs font-['Inter'] hover:underline"
                        >
                          <Edit2 className="w-3 h-3" /> Edit
                        </Link>
                        <a
                          href={`/work#${c.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-white/40 text-xs font-['Inter'] hover:text-white"
                        >
                          <ExternalLink className="w-3 h-3" /> View
                        </a>
                      </div>
                      <button
                        onClick={() => handleDelete(c.id)}
                        data-testid={`case-delete-${c.id}`}
                        className="text-red-400/70 hover:text-red-400 p-1"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCaseStudies;
